import React, { useEffect, useState } from 'react';
import CarouselScreen from '../../../components/CarouselScreen';
import tdSphere from '../../../assets/images/td_sphere.png';
import { auth, db } from '../../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getInformantDashboardPath, getPatientDashboardPath } from '../../../utils/informantDashboardRouting';
import { getServiceUserProfileDocRef } from '../../../utils/informantFirestorePaths';

export default function Welcome() {  
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mark that the user has seen the welcome screens
  const markWelcomeSeen = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        const idTokenResult = await user.getIdTokenResult();
        const userRef = getServiceUserProfileDocRef(user.uid, idTokenResult.claims);
        await updateDoc(userRef, {
          hasSeenWelcome: true,
          welcomeSeenAt: serverTimestamp()
        });
        console.log("User marked as having seen welcome");
      } catch (error) {
        console.error("Error updating welcome status:", error);
      }
    }
  };  

  useEffect(() => {
    // Fetch user data and type when component mounts
    const fetchUserData = async () => {
      setLoading(true);
      const user = auth.currentUser;
      
      if (user) {
        try {
          // Get user claims for user type
          const idTokenResult = await user.getIdTokenResult(true);
          const userType = idTokenResult.claims.userType;
          
          // Get user document from the correct collection
          const userRef = getServiceUserProfileDocRef(user.uid, idTokenResult.claims);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.exists() ? userDoc.data() : {};
          
          // Create slides based on user type, data, and claims (for routing)
          createSlides(userType, userData, idTokenResult.claims);
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    // Create slides based on user type and data
    const createSlides = (userType, userData, claims) => {
      // Base slide templates
      let slideTemplates = [];
      
      if (userType === 'Informant') {
        const informantRoute = getInformantDashboardPath(claims);
        const pathwayLabel = claims?.assessmentModule === 'adultAutism' ? 'Autism' : 'ADHD';
        slideTemplates = [
          {
            title: "Welcome",
            content: [
              `Thank you for supporting <em>[PATIENT NAME]'s</em> ${pathwayLabel} assessment with the <em>[PROVIDER NAME]</em> app.`,
              "Your insights will help us understand their unique experiences.",
              "All information is confidential and stored securely."
            ],
            footnoteText: "Developed with care in partnership with ThinkDivergent",
            ctaText: "Skip",
            route: informantRoute
          },
          {
            title: "How It Works",
            content: [
              "You'll answer questions about <em>[PATIENT NAME]'s</em> experiences.",
              "There are no right or wrong answers - just your perspective.",
              "Take breaks anytime - your comfort matters."
            ],
            ctaText: "Skip",
            route: informantRoute
          },
          {
            title: "Ready to Begin?",
            content: [
              "Your input is really valuable.",
              "You will be helping <em>[PATIENT NAME]</em> understand more about themself."
            ],
            ctaText: "Get Started",
            route: informantRoute
          }
        ];
      } else {
        // Patient slides
        const isSubclinicalService = userData?.organisation === 'ThinkDivergent';
        const patientRoute = getPatientDashboardPath(claims);
        
        slideTemplates = [
          {
            title: "Welcome",
            content: [
              "This platform helps you share information about your experiences in a way that feels safe and meaningful to you.",
              isSubclinicalService 
                ? "Your responses will support a personalised and supportive discussion."
                : "Your responses will support a personalised and supportive assessment.",
              "Your answers are confidential and stored securely."
            ],
            footnoteText: isSubclinicalService ? undefined : "Developed with care in partnership with ThinkDivergent",
            ctaText: "Skip",
            route: patientRoute
          },
          {
            title: "How It Works",
            content: [
              "We'll guide you through questions about your experiences.",
              "You're in control - share what you feel comfortable with.",
              "There are no right or wrong answers, just your unique story."
            ],
            ctaText: "Skip",
            route: patientRoute
          },
          {
            title: "Ready to Begin?",
            content: [
              "Each set of questions takes about 10 minutes.",
              "Pause and take breaks whenever you need.",
              "This is a step toward learning more about yourself in a safe space."
            ],
            ctaText: "Get Started",
            route: patientRoute
          }
        ];
      }
      
      // Replace placeholders in content
      const processedSlides = slideTemplates.map(slide => {
        // Add onClickCta to all slides
        slide.onClickCta = markWelcomeSeen;
        
        // Replace placeholders in content
        if (userData) {
          slide.content = slide.content.map(text => {
            let processedText = text;
            
            // Replace [PROVIDER NAME] with organisation
            if (userData.organisation) {
              processedText = processedText.replace(/\[PROVIDER NAME\]/g, userData.organisation);
            }
            
            // For Informants, replace [PATIENT NAME] with the linked patient's name.
            // ADHD informants store this in createdByName; Autism and CYP
            // informants in activeAssignments.{module}.{patientId}.patientFirstName.
            if (userType === 'Informant') {
              const assignedPatientName = (namespace) => {
                const assignments = userData?.activeAssignments?.[namespace];
                if (!assignments) return '';
                const entry = Object.values(assignments).find(e => e && e.status !== 'deactivated');
                return entry?.patientFirstName || Object.values(assignments)[0]?.patientFirstName || '';
              };

              // CYP is checked FIRST because createdByName is the name of
              // whoever created the informant record, and for CYP that is the
              // clinician — so a parent was thanked for supporting "Ms. <their
              // clinician>'s ADHD assessment". The assignment index always
              // carries the child. Adult informants have no cyp_adhd namespace,
              // so their existing order is untouched.
              const patientName = assignedPatientName('cyp_adhd')
                || userData.createdByName
                || assignedPatientName('adult_autism');
              if (patientName) {
                processedText = processedText.replace(/\[PATIENT NAME\]/g, patientName);
              }
            }
            
            return processedText;
          });
        }
        
        return slide;
      });
      
      setSlides(processedSlides);
    };
    
    fetchUserData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-violet-900">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <CarouselScreen 
      slides={slides}
      bgColor="bg-violet-900"
      logo={tdSphere}
      logoAlt="ThinkDivergent Sphere"
      showBackButton={false}
    />
  );
}